import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  createFilterOptions,
} from '@mui/material';

import { TOKENS } from '../theme';

// Some legacy customer.files entries serialize Windows-style paths
// ("http://host/customer\\23505293\\file.png"). Forward-slash them so
// the browser fetches correctly.
const normalize = (url) => (url ? url.replace(/\\/g, '/') : null);

// Used by BonusEditSheet + RaffleEditSheet. Many customers share full
// names so the picker shows photo + name + phone. Clicking the photo
// expands that row inline to a 200×200 preview so the manager can
// disambiguate without losing the search results.
//
// Why inline (and not a Dialog)? Earlier versions used a full-screen
// zoom Dialog, but MUI's Modal/focus management always collapsed the
// Autocomplete dropdown behind it — so the user lost their search
// state every time they zoomed. Inline expansion keeps everything
// inside the dropdown's popper, with no focus stealing.
//
// Perf: locations carry 1k+ customers. We cap visible matches at 50.
// Avatars use native <img loading="lazy"> so off-screen photos don't
// fetch. Pre-cached _search string keeps filtering cheap.
export default function CustomerPicker({
  value,
  onChange,
  customers = [],
  label = 'Customer*',
  placeholder = 'Search customer...',
  maxOptions = 50,
}) {
  const [expandedId, setExpandedId] = useState(null);

  const enriched = useMemo(
    () =>
      (customers || []).map((c) => ({
        ...c,
        _label: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        _search: `${c.firstName || ''} ${c.lastName || ''} ${
          c.phone || ''
        }`.toLowerCase(),
        _photo: normalize(c.files?.[0]?.localURL),
      })),
    [customers]
  );

  const filter = useMemo(
    () =>
      createFilterOptions({
        limit: maxOptions,
        stringify: (c) => c._search,
        ignoreCase: true,
      }),
    [maxOptions]
  );

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        sx={{ mt: 0.5 }}
        options={enriched}
        value={value}
        onChange={(_e, v) => {
          onChange(v);
          setExpandedId(null);
        }}
        isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
        getOptionLabel={(c) =>
          c?._label ||
          (c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '')
        }
        filterOptions={filter}
        renderInput={(p) => (
          <TextField
            {...p}
            size="small"
            placeholder={`${placeholder} (showing top ${maxOptions})`}
          />
        )}
        renderOption={(props, c) => {
          const fullName = c._label || '—';
          const photo = c._photo;
          const isExpanded = expandedId === c.customerId;
          // Keep MUI's onClick on the <li> so event.currentTarget
          // still points at the correct option element (MUI reads
          // `data-option-index` off currentTarget). The avatar and
          // PREVIEW label use stopPropagation to OPT OUT of the
          // row's selection — clicking the row body still selects.
          const { key, ...liProps } = props;
          return (
            <Box
              component="li"
              {...liProps}
              key={c.customerId}
              sx={{
                display: 'flex !important',
                flexDirection: 'column !important',
                alignItems: 'stretch !important',
                gap: 0.75,
                py: 0.75,
                px: 1.25,
                background: isExpanded ? TOKENS.slate50 : undefined,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  onMouseDown={(e) => {
                    if (photo) {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }}
                  onClick={(e) => {
                    if (!photo) return;
                    // Toggle inline preview without
                    // bubbling to the row's select handler.
                    e.stopPropagation();
                    setExpandedId(isExpanded ? null : c.customerId);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: TOKENS.slate100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: TOKENS.slate700,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: photo ? 'zoom-in' : 'default',
                    outline: isExpanded
                      ? `2px solid ${TOKENS.blue600}`
                      : 'none',
                    // Re-enable pointer events for the photo (parent disabled them)
                    pointerEvents: 'auto',
                  }}
                >
                  {photo ? (
                    <img
                      src={photo}
                      alt=""
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    fullName.charAt(0).toUpperCase()
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TOKENS.slate900,
                    }}
                    noWrap
                  >
                    {fullName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: TOKENS.slate500,
                      fontFamily: 'monospace',
                    }}
                    noWrap
                  >
                    {c.phone || '—'}
                  </Typography>
                </Box>
                {photo && (
                  <Typography
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedId(isExpanded ? null : c.customerId);
                    }}
                    sx={{
                      fontSize: 10,
                      color: TOKENS.blue700,
                      fontWeight: 700,
                      flexShrink: 0,
                      cursor: 'pointer',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      pointerEvents: 'auto',
                      '&:hover': { background: TOKENS.blue50 },
                    }}
                  >
                    {isExpanded ? 'HIDE' : 'PREVIEW'}
                  </Typography>
                )}
              </Box>
              {isExpanded && photo && (
                <Box
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    background: TOKENS.slate100,
                    borderRadius: 1,
                    p: 1,
                    pointerEvents: 'auto',
                  }}
                >
                  <Box
                    component="img"
                    src={photo}
                    alt={fullName}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 220,
                      borderRadius: 1,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
}
